import axios from 'axios';
import { apiService } from '../../../components/api/apiService';
import { getOrchestrationFoBaseUrl } from '../workflows/orchestrationFoUrl';
import { logGovernedMcp } from './governedMcpLogger';
import { isBlankWorkflowTemplate, isLeftoverDemoSample, parseWorkflowPayload, stringifyWorkflowPayload, workflowTriggerPayload } from './publishSchemaBuilder';
import type { WorkflowGraphSource, WorkflowInputField, WorkflowReplyMode, WorkflowSource } from './governedMcpTypes';

export type StudioProject = {
  id: string;
  name: string;
  description: string;
  workflowIds: string[];
};

const studioFoClient = axios.create({ timeout: 20000 });

function foStudioBase(): string {
  return `${getOrchestrationFoBaseUrl()}/studio-api`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const rec = asRecord(value);
  if (!rec) return [];
  for (const key of ['items', 'data', 'projects', 'workflows', 'results']) {
    if (Array.isArray(rec[key])) return rec[key] as unknown[];
  }
  return [];
}

function asText(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function asIdList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => asText(item)).filter(Boolean);
}

function parseJson(value: unknown): unknown {
  if (typeof value !== 'string' || !value.trim()) return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function collectNodes(raw?: Record<string, unknown> | null): unknown[] {
  if (!raw) return [];
  const bags = [
    raw.nodes,
    asRecord(raw.graph)?.nodes,
    asRecord(raw.data)?.nodes,
    asRecord(raw.workflow)?.nodes,
    asRecord(raw.revision)?.nodes,
    asRecord(parseJson(raw.definition))?.nodes,
    asRecord(parseJson(raw.workFlowJson || raw.work_flow_json))?.nodes,
  ];
  for (const bag of bags) {
    if (Array.isArray(bag) && bag.length) return bag;
  }
  return [];
}

function nodeConfig(node: Record<string, unknown>): Record<string, unknown> {
  return (
    asRecord(node.config) ||
    asRecord(asRecord(node.data)?.config) ||
    asRecord(asRecord(node.settings)?.config) ||
    asRecord(node.data) ||
    asRecord(node.settings) ||
    {}
  );
}

function isInputNode(node: Record<string, unknown>): boolean {
  const type = asText(node.type || node.kind || asRecord(node.data)?.type).toLowerCase();
  return type === 'input' || type === 'trigger' || type === 'triggernode' || type === 'start';
}

function inputConfig(raw?: Record<string, unknown> | null): Record<string, unknown> {
  for (const node of collectNodes(raw)) {
    const rec = asRecord(node);
    if (!rec || !isInputNode(rec)) continue;
    const config = nodeConfig(rec);
    if (Object.keys(config).length) return config;
  }
  return {};
}

export function replyModeFromConfig(config: Record<string, unknown>): WorkflowReplyMode {
  const mode = asText(config.httpReplyMode || config.replyMode).toLowerCase();
  return mode.includes('wait') ? 'wait_for_result' : 'acknowledge';
}

export function graphSourceFromWorkflow(raw?: Record<string, unknown> | null): WorkflowGraphSource {
  return asText(raw?.status).toLowerCase().includes('publish') ? 'revision' : 'draft';
}

export function fieldsFromConfig(config: Record<string, unknown>): { fields: WorkflowInputField[]; samplePayloadJson: string | null } {
  const manual = asRecord(config.manual) || {};
  const hints = asRecord(config.runHints) || {};
  const schema = asRecord(config.schema) || asRecord(manual.schema);
  const schemaProps = asRecord(schema?.properties);
  const parsed = parseWorkflowPayload(manual.samplePayloadJson || config.samplePayloadJson);
  const declared = [
    ...asIdList(config.requiredFields),
    ...asIdList(config.payloadKeys),
    ...asIdList(hints.payloadKeys),
    ...asIdList(schema?.required),
    ...(schemaProps ? Object.keys(schemaProps) : []),
  ];
  const formFields = [
    ...(Array.isArray(config.formFields) ? config.formFields : []),
    ...(Array.isArray(manual.formFields) ? manual.formFields : []),
  ];
  const fields: WorkflowInputField[] = [];
  const seen = new Set<string>();
  formFields.forEach((item) => {
    const rec = asRecord(item);
    const key = asText(rec?.key || rec?.id || rec?.name);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const optionList = rec?.options;
    const options = Array.isArray(optionList) ? optionList.map((item) => asText(item)).filter(Boolean) : [];
    fields.push({
      key,
      label: asText(rec?.label) || key,
      dataType: asText(rec?.dataType || rec?.type) || 'string',
      required: rec?.required === true || declared.includes(key),
      helpText: asText(rec?.helpText || rec?.placeholder) || undefined,
      options: options.length ? options : undefined,
    });
  });
  [config.variables, asRecord(config.settings)?.variables, manual.variables].forEach((bag) => {
    if (!Array.isArray(bag)) return;
    bag.forEach((item) => {
      const rec = asRecord(item);
      const key = asText(rec?.name || rec?.key || rec?.id);
      if (!key || seen.has(key)) return;
      seen.add(key);
      fields.push({
        key,
        label: asText(rec?.label) || key,
        dataType: asText(rec?.dataType || rec?.type) || 'string',
        required: rec?.required !== false,
        helpText: asText(rec?.helpText || rec?.placeholder) || undefined,
      });
    });
  });
  declared.forEach((key) => {
    if (seen.has(key)) return;
    seen.add(key);
    fields.push({
      key,
      label: key,
      dataType: Array.isArray(parsed[key]) ? 'array' : 'string',
      required: true,
    });
  });
  if ((!fields.length || isBlankWorkflowTemplate(parsed)) && !isLeftoverDemoSample(parsed)) {
    Object.keys(parsed).forEach((key) => {
      if (seen.has(key)) return;
      seen.add(key);
      fields.push({
        key,
        label: key,
        dataType: Array.isArray(parsed[key]) ? 'array' : 'string',
        required: true,
      });
    });
  }
  const matched: Record<string, unknown> = {};
  fields.forEach((field) => {
    if (field.key in parsed) matched[field.key] = parsed[field.key];
  });
  return {
    fields,
    samplePayloadJson: stringifyWorkflowPayload(Object.keys(matched).length ? matched : workflowTriggerPayload(fields)),
  };
}

export function toStudioProject(raw: unknown): StudioProject | null {
  const rec = asRecord(raw);
  const id = asText(rec?.id);
  if (!id) return null;
  return {
    id,
    name: asText(rec?.name) || id,
    description: asText(rec?.description),
    workflowIds: asIdList(rec?.workflowIds),
  };
}

export function toWorkflowSource(raw: unknown, projectId?: string | null, projectName?: string): WorkflowSource | null {
  const row = asRecord(raw) || {};
  const id = asText(row.id);
  if (!id) return null;
  const config = inputConfig(row);
  const publish = fieldsFromConfig(config);
  const replyMode = replyModeFromConfig(config);
  return {
    id,
    name: asText(row.name) || id,
    code: asText(row.code) || id,
    description: asText(row.description),
    longRunning: replyMode === 'wait_for_result',
    destructive: false,
    hasSideEffects: true,
    isIdempotent: false,
    requiresApproval: false,
    status: asText(row.status) || 'draft',
    activationStatus: asText(row.activationStatus) || 'active',
    version: Number(row.version) || 0,
    tags: asIdList(row.tags),
    projectId: asText(projectId ?? row.projectId) || null,
    projectName,
    replyMode,
    graphSource: graphSourceFromWorkflow(row),
    inputFields: publish.fields,
    samplePayloadJson: publish.samplePayloadJson,
    triggerType: asText(config.triggerType) || 'manual',
  };
}

export function isPublishedWorkflow(workflow: WorkflowSource): boolean {
  return asText(workflow.status).toLowerCase() === 'published';
}

function assignProjects(projects: StudioProject[], workflows: WorkflowSource[]): WorkflowSource[] {
  const byWorkflow = new Map<string, StudioProject>();
  projects.forEach((project) => {
    project.workflowIds.forEach((id) => byWorkflow.set(id, project));
  });
  return workflows.map((item) => {
    if (item.projectId) {
      const named = projects.find((project) => project.id === item.projectId);
      return { ...item, projectName: named?.name || item.projectName };
    }
    const project = byWorkflow.get(item.id);
    return project ? { ...item, projectId: project.id, projectName: project.name } : item;
  });
}

async function foGet(path: string): Promise<unknown> {
  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenant_id');
  const response = await studioFoClient.get(path, {
    baseURL: foStudioBase(),
    withCredentials: false,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
    },
  });
  return response.data;
}

async function backendGet(path: string): Promise<unknown> {
  return apiService.get(path, {}, 'backend_py');
}

async function loadList(foPath: string, backendPath: string): Promise<unknown[]> {
  try {
    const rows = asList(await foGet(foPath));
    if (rows.length) return rows;
  } catch (err) {
    logGovernedMcp('warning', 'studio_fo_catalog_unavailable', {
      path: foPath,
      message: err instanceof Error ? err.message : 'fo_failed',
    });
  }
  return asList(await backendGet(backendPath));
}

async function loadRow(foPath: string, backendPath: string): Promise<unknown> {
  try {
    const row = await foGet(foPath);
    if (row) return row;
  } catch (err) {
    logGovernedMcp('warning', 'studio_fo_row_unavailable', {
      path: foPath,
      message: err instanceof Error ? err.message : 'fo_failed',
    });
  }
  try {
    return await backendGet(backendPath);
  } catch (err) {
    logGovernedMcp('warning', 'studio_backend_row_unavailable', {
      path: backendPath,
      message: err instanceof Error ? err.message : 'backend_failed',
    });
    return null;
  }
}

export const studioCatalogService = {
  async load(): Promise<{ projects: StudioProject[]; workflows: WorkflowSource[] }> {
    logGovernedMcp('app', 'studio_catalog_load');
    const [projectRows, workflowRows] = await Promise.all([
      loadList('/api/v1/projects', '/api/v1/studio/projects'),
      loadList('/api/v1/workflows', '/api/v1/studio/workflows'),
    ]);
    const allProjects = projectRows.map(toStudioProject).filter((item): item is StudioProject => Boolean(item));
    const workflows = assignProjects(
      allProjects,
      workflowRows.map((row) => toWorkflowSource(row)).filter((item): item is WorkflowSource => Boolean(item)),
    ).filter(isPublishedWorkflow);
    const usedProjects = new Set(workflows.map((item) => item.projectId).filter(Boolean));
    const projects = allProjects.filter((project) => usedProjects.has(project.id));
    logGovernedMcp('app', 'studio_catalog_load_ok', { projects: projects.length, workflows: workflows.length });
    return { projects, workflows };
  },

  async getById(id: string): Promise<WorkflowSource | null> {
    const trimmed = id.trim();
    if (!trimmed) return null;
    const row = await loadRow(
      `/api/v1/workflows/${encodeURIComponent(trimmed)}`,
      `/api/v1/studio/workflows/${encodeURIComponent(trimmed)}`,
    );
    const rec = asRecord(row);
    const body = asRecord(rec?.data) || asRecord(rec?.workflow) || rec || {};
    const revision = asRecord(rec?.revision) || asRecord(body.revision);
    const graph = asRecord(revision) || asRecord(body.graph);
    const workflow = toWorkflowSource({
      ...body,
      nodes: Array.isArray(body.nodes) ? body.nodes : graph?.nodes,
      graph: asRecord(body.graph) || graph,
    });
    logGovernedMcp('app', 'studio_workflow_detail', {
      workflowId: trimmed,
      fields: workflow?.inputFields?.length || 0,
      hasSample: Boolean(workflow?.samplePayloadJson),
    });
    return workflow;
  },
};
